import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  Box,
  Typography,
  Button,
  ThemeProvider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-lists/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import NavBar from '../../../pieces/navbar/navbar';
import myTheme from '../../../styles/myStyles';
import {useAppStore} from '../../../stores/appStore';
import axios from 'axios';
import ApiUtils from '../../../utils/apiUtils';
import base_url from '../../../baseUrls';

/** Recent AI calls board; must render inside `AppStoreProvider` (see aiDashboard.js). */
export const AiCurrentCalls = ({showAll = false} = {}) => {
  const recentCallsRefreshIntervalSeconds = 60;
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [recentCalls, setRecentCalls] = useState([]);
  const [recentCallsLoading, setRecentCallsLoading] = useState(true);
  const [recentCallsError, setRecentCallsError] = useState('');
  // const [agents, setAgents] = useState([]);
  // const [agentsLoading, setAgentsLoading] = useState(true);
  // const [agentsError, setAgentsError] = useState('');
  const [refreshCountdown, setRefreshCountdown] = useState(recentCallsRefreshIntervalSeconds);
  const isRecentCallsFetchingRef = useRef(false);
  const isRecentCallsMountedRef = useRef(false);
  // const isAgentsFetchingRef = useRef(false);
  // const isAgentsMountedRef = useRef(false);

  const user = useSelector((state) => state.user);

  const {
    addUser,
    getCurrentUserCallStatus,
    getCurrentUserCallStatusDetails,
    getCurrentUserLogs,
    getUsersWithCallStatus,
    isAutoCallPaused,
    isCallInProgress,
    removeUser,
    selectedUser,
    setAutoCallPaused,
    setSelectedUser,
    startCall,
    updateUser,
  } = useAppStore();
  const users = getUsersWithCallStatus;
  const selectedUserId = selectedUser?.user_id || null;
  const isUserLoggedIn = user?.isAuthenticated || false;
  const isCallStarted = isCallInProgress || isAutoCallPaused;

  const callStatus = getCurrentUserCallStatus;
  const callStatusDetail = getCurrentUserCallStatusDetails;
  const callLogs = getCurrentUserLogs;

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
  };

  const handleToggleAutoCall = async (start) => {

    //console.log('inside handleToggleAutoCall');
    //console.log('start', start);
    //console.log('selectedUserId', selectedUserId);

    if (start) {
      if (!selectedUserId) {
        return;
      }

      setAutoCallPaused(false);
      await startCall(selectedUserId);
    } else {
      setAutoCallPaused(true);
    }
  };

  const handleReportOk = ({start, end}) => {
    console.log('Report from', start, 'to', end);
  };

  const handleReportCancel = () => {
    // Optional: handle cancel action
  };

  // const fetchAgents = useCallback(async ({showLoader = false} = {}) => {
  //   if (isAgentsFetchingRef.current) {
  //     return;
  //   }

  //   isAgentsFetchingRef.current = true;

  //   if (showLoader && isAgentsMountedRef.current) {
  //     setAgentsLoading(true);
  //   }

  //   if (isAgentsMountedRef.current) {
  //     setAgentsError('');
  //   }

  //   try {
  //     const authToken = await ApiUtils.getAccessToken();
  //     const response = await axios.get(`${base_url.api}ai/call-agents`, {
  //       headers: {Authorization: authToken}
  //     });

  //     const responseData = response?.data?.data?.aiCallAgents;
  //     const agentList = Array.isArray(responseData) ? responseData : [];

  //     if (isAgentsMountedRef.current) {
  //       setAgents(agentList);
  //     }
  //   } catch (error) {
  //     if (isAgentsMountedRef.current) {
  //       setAgentsError('Unable to load agents.');
  //       setAgents([]);
  //     }
  //   } finally {
  //     if (showLoader && isAgentsMountedRef.current) {
  //       setAgentsLoading(false);
  //     }

  //     isAgentsFetchingRef.current = false;
  //   }
  // }, []);

  const fetchRecentCalls = useCallback(async ({showLoader = false} = {}) => {
    if (isRecentCallsFetchingRef.current) {
      return;
    }

    isRecentCallsFetchingRef.current = true;

    if (showLoader && isRecentCallsMountedRef.current) {
      setRecentCallsLoading(true);
    }

    if (isRecentCallsMountedRef.current) {
      setRecentCallsError('');
    }
 
    try {
      const authToken = await ApiUtils.getAccessToken();
      const response = await axios.get(`${base_url.api}ai/recent-calls`, {
        headers: {Authorization: authToken},
        params: {
          aiAgentId: user?.aiAgentId,
          showAll,
        },
      });

      const responseData = response?.data?.data?.aiCalls;
      const calls = Array.isArray(responseData) ? responseData : [];

      if (isRecentCallsMountedRef.current) {
        setRecentCalls(calls);
      }
    } catch (error) {
      if (isRecentCallsMountedRef.current) {
        setRecentCallsError('Unable to load recent AI calls.');
        setRecentCalls([]);
      }
    } finally {
      if (showLoader && isRecentCallsMountedRef.current) {
        setRecentCallsLoading(false);
      }

      isRecentCallsFetchingRef.current = false;
    }
  }, [user?.viewAgentAiCall, user?.agentId, showAll]);

  const handleManualRefresh = () => {
    setRefreshCountdown(recentCallsRefreshIntervalSeconds);
    fetchRecentCalls();
    // fetchAgents();
  };

  const refreshGrid = () => {
    handleManualRefresh();
  };

  const getAdditionalButtons = () => [
    {
      key: 1,
      label: 'Refresh',
      variant: 'outlined',
      color: 'primary',
      onClick: refreshGrid,
    },
  ];

  useEffect(() => {
    isRecentCallsMountedRef.current = true;
    // isAgentsMountedRef.current = true;

    fetchRecentCalls({showLoader: true});
    // fetchAgents({showLoader: true});
    const refreshIntervalId = setInterval(() => {
      if (isRecentCallsMountedRef.current) {
        setRefreshCountdown(recentCallsRefreshIntervalSeconds);
      }

      fetchRecentCalls();
      // fetchAgents();
    }, recentCallsRefreshIntervalSeconds * 1000);
    const countdownIntervalId = setInterval(() => {
      if (isRecentCallsMountedRef.current) {
        setRefreshCountdown((previousValue) => (
          previousValue <= 1 ? recentCallsRefreshIntervalSeconds : previousValue - 1
        ));
      }
    }, 1000);

    return () => {
      isRecentCallsMountedRef.current = false;
      // isAgentsMountedRef.current = false;
      clearInterval(refreshIntervalId);
      clearInterval(countdownIntervalId);
    };
  }, [fetchRecentCalls, recentCallsRefreshIntervalSeconds]);

  const formatRecentCallDate = (value) => {
    if (!value) {
      return 'No timestamp';
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? String(value) : parsedDate.toLocaleString();
  };

  /** @param {string|number|Date} value */
  const formatUpdatedAt = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return String(value);
    }

    const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const dd = String(parsedDate.getDate()).padStart(2, '0');
    const yy = String(parsedDate.getFullYear()).slice(-2);
    let hour12 = parsedDate.getHours() % 12;
    hour12 = hour12 === 0 ? 12 : hour12;
    const hh = String(hour12).padStart(2, '0');
    const min = String(parsedDate.getMinutes()).padStart(2, '0');
    const ampm = parsedDate.getHours() >= 12 ? 'pm' : 'am';

    return `${mm}/${dd}/${yy} ${hh}:${min} ${ampm}`;
  };

  const getRecentCallNameParts = (call, index) => {
    const fallbackName = call.customer_name || call.customerName || call.name || `Call ${index + 1}`;
    const firstName = call.first_name || call.firstName || call.customer_first_name || call.customerFirstName;
    const lastName = call.last_name || call.lastName || call.customer_last_name || call.customerLastName;

    if (firstName || lastName) {
      return {
        firstName: firstName || 'Unknown',
        lastName: lastName || '',
      };
    }

    const [derivedFirstName = fallbackName, ...remainingNameParts] = String(fallbackName).trim().split(/\s+/);

    return {
      firstName: derivedFirstName,
      lastName: remainingNameParts.join(' '),
    };
  };

  const truncateText = (value, maxLength) => {
    const text = String(value || '');
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const buildInspectionModuleUrl = (inspectionId) => {
    const idString = String(inspectionId ?? '').trim();
    if (!idString) {
      return '';
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const id = encodeURIComponent(idString);

    const url = `${origin}/module/${id}?moduleName=inspection&items=50&page=1&showSameAddressJobs=true`;

    return url;
  };

  // const agentItems = agents.map((agent, index) => {
  //   const firstName = agent.first_name || agent.firstName || 'Unknown';
  //   const lastName = agent.last_name || agent.lastName || '';
  //   const name =
  //     agent.name ||
  //     agent.full_name ||
  //     agent.fullName ||
  //     `${firstName}${lastName ? ` ${lastName}` : ''}`;

  //   return {
  //     id: agent.id || agent.user_id || agent.agent_id || `${index}`,
  //     firstName,
  //     lastName,
  //     name,
  //     enabled_for_calls: !!(agent.enabled_for_calls ?? agent.enabledForCalls),
  //   };
  // });

  const formatCallDetailSpeakerLabel = (speakerLabel) => {
    const lower = speakerLabel.toLowerCase();
    if (lower === 'user:') {
      return 'HO:  ';
    }
    if (lower === 'assistant:') {
      return 'Agent:  ';
    }
    if (lower === 'agent-action:') {
      return 'Agent Action:  ';
    }
    return speakerLabel;
  };

  const renderCallDetails = (callDetails) => (
    String(callDetails).split('\n').map((line, index) => {
      const matchedLine = line.match(/^(\s*)(user:|assistant:|agent-action:)(.*)$/i);

      if (!matchedLine) {
        return (
          <Box key={`${line}-${index}`} component="span" sx={{display: 'block'}}>
            {line}
          </Box>
        );
      }

      const [, leadingWhitespace, speakerLabel, remainingText] = matchedLine;
      const isUserLine = speakerLabel.toLowerCase() === 'user:';

      return (
        <Box
          key={`${line}-${index}`}
          component="span"
          sx={{
            backgroundColor: isUserLine ? '#e3f2fd' : 'transparent',
            borderRadius: '4px',
            display: 'block',
            px: isUserLine ? 0.5 : 0,
          }}
        >
          {leadingWhitespace}
          <Box component="span" sx={{fontWeight: 700}}>
            {formatCallDetailSpeakerLabel(speakerLabel)}
          </Box>
          {remainingText}
        </Box>
      );
    })
  );

  const renderRecentCallItem = (item) => {

    //console.log('item', item);

    const isCompleted = item.status?.toLowerCase() === 'completed';
    const displayStatus = isCompleted
      ? 'Call Made'
      : item.status
        ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
        : item.status;
    const rawInspectionJobId =
      item.inspection_job_id ??
      item.inspectionJobId ??
      item.inspection_id ??
      item.inspectionId;
    const inspectionJobIdStr =
      rawInspectionJobId == null || rawInspectionJobId === ''
        ? ''
        : String(rawInspectionJobId).trim();
    const hasInspectionJobLink = inspectionJobIdStr.length > 0;

    return (
      <Box
        className="e-card e-card-horizontal"
        sx={{
          backgroundColor: isCompleted ? '#e8f5e9' : '#fff3e0',
          border: '2px solid #d0d7de',
          borderRadius: '12px',
          boxSizing: 'border-box',
          minWidth: 0,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          sx={{
            alignItems: 'flex-start',
            borderBottom: '2px solid #d0d7de',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            px: 1.5,
            py: 0.75,
          }}
        >
          <Typography sx={{fontSize: '1.00rem', fontWeight: 600, lineHeight: 1.2, minWidth: 0}}>
            {`Homeowner: ${item.first_name} ${item.last_name} (${item.phone})`}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{fontSize: '1rem', textAlign: 'right', wordBreak: 'break-word'}}
          >
            {item.principal}
          </Typography>
        </Box>
        <Box sx={{backgroundColor: '#ffffff', px: 2, py: 1.5}}>
          <TableContainer
            component={Box}
            sx={{width: '100%', mb: 1.5, overflowX: 'auto'}}
          >
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: '100%',
                borderCollapse: 'collapse',
                '& .MuiTableCell-root': {
                  borderBottom: 'none',
                  borderColor: 'divider',
                  fontSize: '0.95rem',
                  padding: '2px 8px 2px 0',
                  verticalAlign: 'top',
                  wordBreak: 'break-word',
                  width: '25%',
                },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography
                      component="div"
                      sx={{fontSize: '0.95rem', wordBreak: 'break-word', color: 'text.primary', fontWeight: 400}}
                    >
                      <Box component="span" sx={{fontWeight: 700}}>Job #</Box>
                      <Box component="span" sx={{fontWeight: 400}}>: </Box>
                      {hasInspectionJobLink ? (
                        <Link
                          href={buildInspectionModuleUrl(inspectionJobIdStr)}
                          color="inherit"
                          underline="always"  
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.job_id ?? ''}
                        </Link>
                      ) : (
                        <Box component="span" sx={{fontWeight: 400}}>{item.job_id ?? ''}</Box>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="div"
                      sx={{fontSize: '0.95rem', wordBreak: 'break-word', color: 'text.primary', fontWeight: 400}}
                    >
                      <Box component="span" sx={{fontWeight: 700}}>Status</Box>
                      <Box component="span" >: </Box>
                      <Box component="span" >{displayStatus}</Box>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="div"
                      sx={{fontSize: '0.95rem', wordBreak: 'break-word', color: 'text.primary', fontWeight: 400}}
                    >
                      <Box component="span" sx={{fontWeight: 700}}>Result</Box>
                      <Box component="span" >: </Box>
                      <Box component="span" >{item.result}</Box>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="div"
                      sx={{fontSize: '0.95rem', wordBreak: 'break-word', color: 'text.primary', fontWeight: 400}}
                    >
                      <Box component="span" sx={{fontWeight: 700}}>Call Made at</Box>
                      <Box component="span" >: </Box>
                      <Box component="span" >{formatUpdatedAt(item.updated_at)}</Box>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TableContainer
            component={Box}
            sx={{width: '100%', mb: 1.5, overflowX: 'auto'}}
          >
            <Table
              size="small"
              sx={{
                tableLayout: 'auto',
                width: '100%',
                borderCollapse: 'collapse',
                '& .MuiTableCell-root': {
                  borderBottom: 'none',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  fontSize: '0.95rem',
                  padding: '2px 8px 2px 0',
                  verticalAlign: 'top',
                  wordBreak: 'break-word',
                },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell sx={{fontWeight: 'bold', whiteSpace: 'nowrap', width: '1%'}}>Address</TableCell>
                  <TableCell>{item.address}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{width: '100%'}}>
            <Typography
              component="div"
              sx={{fontWeight: 'bold', fontSize: '0.95rem', color: 'text.secondary', mb: 0.5}}
            >
              Call Details
            </Typography>
            <TableContainer component={Box} sx={{width: '100%', overflowX: 'auto'}}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'auto',
                  width: '100%',
                  borderCollapse: 'collapse',
                  '& .MuiTableCell-root': {
                    borderBottom: 'none',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    fontSize: '0.95rem',
                    padding: '2px 0',
                    verticalAlign: 'top',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell>
                      {item.callDetails ? (
                        <Box>{renderCallDetails(item.callDetails)}</Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>
    );
  };
  return (
    <>
      <NavBar />
      <ThemeProvider theme={myTheme}>
        <Box sx={{height: '100%', mt: 0, pt: 0}}>
          <Box sx={{mx: 3, mt: '6px', pt: 0}}>
            <div className="e-card e-card-vertical" style={{width: '100%'}}>
              <div
                className="e-card-header"
                style={{alignItems: 'center', marginBottom: '6px', borderBottom: '2px solid #d0d7de', display: 'flex', justifyContent: 'space-between'}}
              >
                <div className="e-card-header-caption">
                  <div className="e-card-header-title" style={{fontSize: '20px', paddingBottom: '10px'}}>
                    Recent AI Calls
                  </div>
                </div>
                <Box sx={{alignItems: 'center', display: 'flex', gap: 1.5, pr: 2}}>
                  <Typography color="text.secondary" sx={{fontSize: '0.95rem', fontWeight: 600, whiteSpace: 'nowrap'}}>Refresh in:</Typography>
                  <Typography
                    color="text.secondary"
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      width: '12px',
                      flexShrink: 0,
                      textAlign: 'right',
                    }}
                  >
                    {refreshCountdown}
                  </Typography>
                  <Typography color="text.secondary" sx={{fontSize: '0.95rem', fontWeight: 600, whiteSpace: 'nowrap', marginRight: '4px'}}>seconds</Typography>

                  {getAdditionalButtons().map((button) => (
                    <Button
                      key={button.key}
                      size="small"
                      variant={button.variant}
                      color={button.color}
                      onClick={button.onClick}
                      disabled={recentCallsLoading || isRecentCallsFetchingRef.current}
                      sx={{
                        textTransform: 'uppercase',
                        lineHeight: '16px',
                      }}
                    >
                      {button.label}
                    </Button>
                  ))}
                </Box>
                  <Typography color="text.secondary" sx={{fontSize: '0.95rem', fontWeight: 600, whiteSpace: 'nowrap'}}>
                    Total Calls: {recentCalls.length ?? 0}
                  </Typography>
              </div>
              <div className="e-card-content">
                <Box sx={{alignItems: 'flex-start', display: 'flex', gap: 2, width: '100%'}}>
                  <Box sx={{flex: '1 1 0%', minWidth: 0, width: '100%'}}>
                    {recentCallsLoading && (
                      <Typography>Loading recent AI calls...</Typography>
                    )}
                    {!recentCallsLoading && recentCallsError && (
                      <Typography color="error">{recentCallsError}</Typography>
                    )}
                    {!recentCallsLoading && !recentCallsError && recentCalls.length === 0 && (
                      <Typography>No recent AI calls found.</Typography>
                    )}
                    {!recentCallsLoading && !recentCallsError && recentCalls.length > 0 && (
                      <Box
                        sx={{
                          alignItems: 'stretch',
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: '1fr',
                          mt: 0,
                          pt: 0,
                          width: '100%',
                        }}
                      >
                        {recentCalls.map((item) => (
                          <Box key={item.id} sx={{minWidth: 0, width: '100%'}}>
                            {renderRecentCallItem(item)}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Available Agents — sidebar (fetchAgents, agents state, agentItems) commented out above
                  <Box
                    sx={{
                      borderLeft: '2px solid #d0d7de',
                      flex: '0 0 15%',
                      minHeight: '100%',
                      pl: 2,
                      width: '15%',
                    }}
                  >
                    <Typography sx={{fontSize: '1rem', fontWeight: 600, mb: 0.5, mt:0.5}}>
                      Available Agents
                    </Typography>
                    {agentsLoading && (
                      <Typography>Loading agents...</Typography>
                    )}
                    {!agentsLoading && agentsError && (
                      <Typography color="error">{agentsError}</Typography>
                    )}
                    {!agentsLoading && !agentsError && agentItems.length === 0 && (
                      <Typography>No agents found.</Typography>
                    )}
                    {!agentsLoading && !agentsError && agentItems.length > 0 && (
                      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
                        {agentItems.map((agent) => (
                          <Box
                            key={agent.id}
                            sx={{
                              alignItems: 'center',
                              border: '1px solid #d0d7de',
                              borderRadius: '8px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              px: 0.5,
                              py: 0.5,
                            }}
                          >
                            <Typography sx={{fontSize: '0.6rem', minWidth: 0}}>
                              {`${agent.name}`}
                            </Typography>
                            <Checkbox checked={agent.enabled_for_calls} disabled inputProps={{'aria-label': `${agent.name} availability`}} />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  */}
                </Box>
              </div>
            </div>
          </Box>
        </Box>
      </ThemeProvider>
    </>
  );
};
